import { IA2UNotificationData, A2UNotification, A2UNotificationStatus } from './types';
import { sleep } from './utils/sleep';

declare namespace FBInstant {
    interface Notification {
        notification_id: string; // "916483332868823"
        schedule_date: string; // "2023-03-01T13:02:31+0000"
        status: 'SENT' | 'SCHEDULED' | 'FAILED' | 'CANCELED'; // maybe others
        label: string;
        user_ASID?: string; // TODO: this needs to be verified
    }
}

export namespace A2U {
    export type Result = { success: boolean; error?: Error };

    const FACEBOOK_CURRENT_USER = 'me';
    const FACEBOOK_MAX_NOTIFICATIONS_TOTAL = 5;
    const FACEBOOK_MAX_NOTIFICATIONS_PER_SESSION = 5;

    let MAX_NOTIFICATIONS_ALLOWED_PER_SESSION = FACEBOOK_MAX_NOTIFICATIONS_PER_SESSION;
    let MAX_NOTIFICATIONS_ALLOWED_TOTAL = FACEBOOK_MAX_NOTIFICATIONS_TOTAL;
    let notificationsScheduledThisSession = 0;

    /**
     * Gets the maximum number of notifications allowed per session
     * @returns {number} The maximum number of notifications
     */
    export function getMaxNotificationsPerSession(): number {
        return MAX_NOTIFICATIONS_ALLOWED_PER_SESSION;
    }

    /**
     * Sets the maximum number of notifications allowed per session
     * @param {number} maxNotifications - The maximum number of notifications
     */
    export function setMaxNotificationsPerSession(maxNotifications: number): void {
        MAX_NOTIFICATIONS_ALLOWED_PER_SESSION = Math.min(FACEBOOK_MAX_NOTIFICATIONS_PER_SESSION, maxNotifications);
    }

    /**
     * Gets the maximum total number of notifications allowed per app
     * @returns {number} The maximum number of notifications
     */
    export function getMaxNotificationsTotal(): number {
        return MAX_NOTIFICATIONS_ALLOWED_TOTAL;
    }

    /**
     * Sets the maximum total number of notifications allowed per app
     * @param {number} maxNotifications - The maximum number of notifications
     */
    export function setMaxNotificationsTotal(maxNotifications: number): void {
        MAX_NOTIFICATIONS_ALLOWED_TOTAL = Math.min(FACEBOOK_MAX_NOTIFICATIONS_TOTAL, maxNotifications);
    }

    /**
     * Schedules notifications to be sent to users.
     * @param {A2UNotification[]} notifications - Array of notifications to be scheduled.
     * @returns {Promise<void>}
     */
    export const scheduleNotifications = async (notifications: A2UNotification[]): Promise<Result[]> => {
        const fetchedNotifications = await fetchNotifications();

        if (!fetchedNotifications) {
            return Promise.resolve(notifications.map(_ => {
                    return {
                        success: false,
                        error: new Error('Failed to fetch notifications')
                    };
                })
            );
        }

        // Get valid scheduled notifications
        const scheduledNotifications = fetchedNotifications.filter(isScheduledNotification);

        // Get notifications that should be canceled based off the allowance for duplicates
        const notificationsToCancel = notifications
            .filter(notification => !(notification.allowDuplicates ?? false)) // Find notifications that don't allow duplicates
            .map(notification => notification.type) // Map to notification type
            .reduce((notificationsToCancel, notificationType) => {
                // Reduce array of types into array of scheduled notifications that should be canceled
                const notificationsWithMatchingType = scheduledNotifications.filter(
                    scheduledNotification => notificationType === JSON.parse(scheduledNotification.label).type
                );
                return [...notificationsToCancel, ...notificationsWithMatchingType];
            }, []);

        const remainingScheduledNotifications = scheduledNotifications.filter(
            notification => !notificationsToCancel.includes(notification)
        );

        // Cancel all the scheduled notifications that are about to be rescheduled
        // FIXME: What if the notification of this type doesn't get sent because of the cap? (imo in all cases I can think of we'd still want it canceled)
        for (let i = 0; i < notificationsToCancel.length; i++) {
            const notificationToCancel = notificationsToCancel[i];
            try {
                await cancelNotification({
                    notificationId: notificationToCancel.notification_id,
                    // FIXME: Still need to verify the name of the user id property on the notification (I <3 fb docs :'c)
                    userASID: notificationToCancel.user_ASID,
                });
            } catch (error) {
                // FIXME: What to do if cancellation fails
                console.error('Failed to cancel notification', notificationToCancel.notification_id);
            }
        }

        const schedulePromises: Promise<Result>[] = [];
        // Send each message
        for (let i = 0; i < notifications.length; i++) {
            const notification = notifications[i];
            const { title, body, mediaUrl, timeoutMs, recipientASID, payload, type } = notification;

            const promise = scheduleNotification({
                title,
                body,
                mediaUrl,
                timeoutMs,
                recipientASID,
                payload,
                label: { type, timeoutMs, sendingTime: new Date(Date.now() + timeoutMs).toString() },
            }).catch(error => {
                console.error(type, error);
                return { success: false, error };
            });
            schedulePromises.push(promise);

            // If not the last notification then wait 1 second to make sure next notification schedule_date is different
            if (i !== notifications.length - 1) {
                // TODO: Improve this, what if the notification fails (maybe due to being at cap already), should we even try the rest of them?
                await sleep(1000);
            }
        }

        // Wait for all promises to settle and map results to expected result
        return (await Promise.allSettled(schedulePromises)).map(result => {
            if (result.status === 'fulfilled') {
                return {
                    ...result.value,
                };
            } else {
                return {
                    success: false,
                    error: result.reason,
                };
            }
        });
    };

    /**
     * Schedules a single notification.
     * @param {IA2UNotificationData} props - The data required to schedule a notification.
     * @returns {Promise<void>}
     */
    const scheduleNotification = async (props: IA2UNotificationData): Promise<Result> => {
        if (notificationsScheduledThisSession > MAX_NOTIFICATIONS_ALLOWED_PER_SESSION) {
            return {
                success: false,
                error: new Error('Notification limit per session has been reached.'),
            };
        }

        notificationsScheduledThisSession++;

        const { title, body, mediaUrl, timeoutMs, recipientASID, payload, label } = props;
        const id = recipientASID ?? FACEBOOK_CURRENT_USER;
        const minAllowedIntervalSeconds = 300;
        const intervalSeconds = Math.floor(timeoutMs / 1000);

        try {
            await FBInstant.graphApi.requestAsync(`/${id}/notifications`, 'POST', {
                message: {
                    title,
                    body,
                    media_url: mediaUrl,
                },
                schedule_interval: intervalSeconds < minAllowedIntervalSeconds ? 0 : intervalSeconds,
                payload: JSON.stringify(payload),
                label: JSON.stringify(label),
            });

            return { success: true };
        } catch (error) {
            return { success: false, error };
        }
    };

    /**
     * Fetches notifications from the user.
     * @returns {Promise<FBInstant.Notification[]>} - An array of the user's notifications.
     */
    export const fetchNotifications = async (): Promise<FBInstant.Notification[]> => {
        const notifications: any = await FBInstant.graphApi.requestAsync('/me/notifications', 'GET');

        return notifications?.data;
    };

    /**
     * Cancels a scheduled notification.
     * @param {Object} props - Object containing notificationId and userASID (optional) properties.
     * @param {string} props.notificationId - The ID of the notification to cancel.
     * @param {string | null} [props.userASID] - The user ASID. Defaults to 'me' if not provided.
     * @returns {Promise<boolean>} - True if the cancellation was successful, false otherwise.
     */
    export const cancelNotification = async (props: {
        notificationId: string;
        userASID?: string | null;
    }): Promise<boolean> => {
        const { notificationId, userASID } = props;
        const id = userASID ?? FACEBOOK_CURRENT_USER;
        let success: boolean = false;

        const result: any = await FBInstant.graphApi.requestAsync(`/${id}/cancel_notification`, 'POST', {
            notification_id: notificationId,
        });

        success = result.success ?? false;

        return success;
    };

    /**
     * Checks if a notification is in the 'SENT' status.
     * @param {FBInstant.Notification} notification - The notification to check.
     * @returns {boolean} - True if the notification is in the 'SENT' status, false otherwise.
     */
    export const isSentNotification = (notification: FBInstant.Notification) => {
        return notification.status === A2UNotificationStatus.SENT;
    };

    /**
     * Checks if a notification is in the 'SCHEDULED' status.
     * @param {FBInstant.Notification} notification - The notification to check.
     * @returns {boolean} - True if the notification is in the 'SCHEDULED' status, false otherwise.
     */
    export const isScheduledNotification = (notification: FBInstant.Notification) => {
        return notification.status === A2UNotificationStatus.SCHEDULED;
    };

    /**
     * Checks if a notification is in the 'CANCELLED' status.
     * @param {FBInstant.Notification} notification - The notification to check.
     * @returns {boolean} - True if the notification is in the 'CANCELLED' status, false otherwise.
     */
    export const isCanceledNotification = (notification: FBInstant.Notification) => {
        return notification.status === A2UNotificationStatus.CANCELED;
    };

    /**
     * Gets the scheduled time of a notification.
     * @param {FBInstant.Notification} notification - The notification to get the time from.
     * @returns {number} - The scheduled time of the notification in milliseconds since the Unix epoch.
     */
    export const getNotificationTime = (notification: FBInstant.Notification): number => {
        return new Date(notification.schedule_date).getTime();
    };
}
